import React, { useState, useEffect, useCallback } from "react";
import "./Sidebar.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";

import sidebarConfig from "../../config/sidebar.json";
import { STORAGE_KEYS, ROUTES } from "../../constant/keyConstants";
import { tokenManager } from "../../utils/Utility";
import { getInitials } from "../../utils/helper";
import { useConstants } from "../../constant/constantProvider";

import { triggerLogout } from "../../store/apiActions";

// Assets – map icon keys from sidebar.json to imported files
import dossierLogo from "../../assets/images/dossier-logo.svg";
import dashboardIcon from "../../assets/images/dashboard-icon.svg";
import dashboardActive from "../../assets/images/active-dashboard-icon.svg";
import promptIcon from "../../assets/images/prompt-manager-icon.svg";
import promptActive from "../../assets/images/active-prompt-manager.svg";
import chatIcon from "../../assets/images/chatBotDisabled.png";
import chatActive from "../../assets/images/chatBot.png";
import logoutIconSvg from "../../assets/images/logout-icon.svg";
import logoutConfirm from "../../assets/images/logoutIcon.png";

const ICON_MAP = {
  dashboardIcon: { default: dashboardIcon, active: dashboardActive },
  promptIcon: { default: promptIcon, active: promptActive },
  chatIcon: { default: chatIcon, active: chatActive },
  logoutIcon: { default: logoutIconSvg, active: logoutIconSvg },
};

/* ── Logout modal ────────────────────────────────────── */
const LogoutModal = ({ show, onConfirm, onCancel, t = {} }) => {
  if (!show) return null;
  return (
    <div className="sidebar-modal-overlay" role="dialog" aria-modal="true">
      <div className="sidebar-modal-card">
        <div className="sidebar-modal-icon">
          <img src={logoutConfirm} alt={t.LOGOUT || "Logout"} />
        </div>
        <p className="sidebar-modal-text">
          {t.LOGOUT_CONFIRMATION || "Are you sure you want to Log Out?"}
        </p>
        <div className="sidebar-modal-actions">
          <button className="button-style primary-button sidebar-modal-btn" onClick={onConfirm}>
            {t.YES || "Yes"}
          </button>
          <button className="button-style outline-button sidebar-modal-btn" onClick={onCancel}>
            {t.NO || "No"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Sidebar ─────────────────────────────────────────── */
const Sidebar = () => {
  const { global: t = {} } = useConstants();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showLogout, setShowLogout] = useState(false);
  const [userInitials, setUserInitials] = useState("U");
  const [userName, setUserName] = useState("User");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_DETAILS);
      if (raw) {
        const parsed = JSON.parse(raw);
        const name = parsed?.full_name || "";
        setUserInitials(getInitials(name));
        setUserName(name.split(" ")[0] || "User");
      }
    } catch { /* ignore */ }
  }, []);


  const handleLogout = useCallback(async () => {
    try {
      if (triggerLogout) {
        await dispatch(triggerLogout());
      }
    } catch (error) {
      console.error(error);
    } finally {
      localStorage.setItem("logout", true);
      toast.success("Logout successful!");
      localStorage.clear();
      tokenManager.removeToken();
      navigate(ROUTES.SIGN_IN);
    }
  }, [dispatch, navigate]);

  return (
    <>
      <aside className="sidebar-root" aria-label="Main navigation">
        {/* Logo */}
        <div className="sidebar-logo">
          <img src={dossierLogo} alt="Dossier" className="sidebar-logo-img" />
        </div>

        {/* Nav items from sidebar.json */}
        <nav className="sidebar-nav">
          {sidebarConfig.sidebarTabs
            .filter((tab) => !tab.hidden && !tab.isHidden)
            .map((tab) => {
              const isDisabled = tab.disabled || tab.isDisabled;
              const isActive = location.pathname === tab.path && !isDisabled;
              const icons = ICON_MAP[tab.icon] ?? ICON_MAP["dashboardIcon"];

              if (isDisabled) {
                return (
                  <div
                    key={tab.id}
                    className="sidebar-nav-item sidebar-nav-item--disabled"
                    title={`${tab.label} (Coming soon)`}
                    aria-disabled="true"
                  >
                    <div className="sidebar-nav-icon-wrap">
                      <img
                        src={icons.default}
                        alt={tab.label}
                        className="sidebar-nav-icon"
                      />
                    </div>
                    <span className="sidebar-nav-label">{tab.label}</span>
                  </div>
                );
              }

              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  className={`sidebar-nav-item${isActive ? " active" : ""}`}
                  title={tab.label}
                  aria-current={isActive ? "page" : undefined}
                >
                  <div className="sidebar-nav-icon-wrap">
                    <img
                      src={isActive ? icons.active : icons.default}
                      alt={tab.label}
                      className="sidebar-nav-icon"
                    />
                  </div>
                  <span className="sidebar-nav-label">{tab.label}</span>
                </Link>
              );
            })}
        </nav>

        {/* Bottom: avatar + profile menu items from sidebar.json */}
        <div className="sidebar-bottom">
          <div className="sidebar-avatar" title={userName} aria-label={`Logged in as ${userName}`}>
            <span>{userInitials}</span>
          </div>

          {sidebarConfig.profileMenuItems.map((item) => {
            if (item.action !== "openLogout") return null;
            const icon = ICON_MAP[item.icon]?.default ?? logoutIconSvg;
            return (
              <button
                key={item.id}
                className="sidebar-logout-btn"
                onClick={() => setShowLogout(true)}
                title="Log Out"
                aria-label="Log Out"
              >
                <img src={icon} alt="Log Out" className="sidebar-logout-icon" />
              </button>
            );
          })}
        </div>
      </aside>

      <LogoutModal
        show={showLogout}
        onConfirm={handleLogout}
        onCancel={() => setShowLogout(false)}
        t={t}
      />
    </>
  );
};

export default Sidebar;